import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AspspsList.css';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl } from '../config/envConfig';
import { startOrchestration, buildAuthSession, getAuthSession } from '../utils/authSession';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';



function AspspsList() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('SE'); // Default to Sweden
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
    const bankDropdownRef = useRef(null);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const countryDropdownRef = useRef(null);
    const [selectedBank, setSelectedBank] = useState(null);
    const [bankOptions, setBankOptions] = useState([]);
    const [selectedBankOption, setSelectedBankOption] = useState(null);
    const [bankOptionsDropdownOpen, setBankOptionsDropdownOpen] = useState(false);
    const bankOptionsDropdownRef = useRef(null);
    const [socialSecurityNumber, setSocialSecurityNumber] = useState('');
    // Last-used SSN (display-formatted). Never prefilled into the input — offered as
    // a clickable suggestion while the field has focus.
    const [ssnSuggestion, setSsnSuggestion] = useState('');
    const [ssnFocused, setSsnFocused] = useState(false);
    const [ssnError, setSsnError] = useState('');
    const [consentError, setConsentError] = useState('');
    // Bank-list fetch failure (missing token, network, non-ok response). Must be
    // user-visible — a silent failure leaves an inexplicably empty bank dropdown.
    const [banksError, setBanksError] = useState(null);
    const [banksFetchAttempt, setBanksFetchAttempt] = useState(0);

    // Dual signing (Nordea). When the selected bank is NDEASESS the user can name one or
    // two authorisers who must each sign. An optional email turns that authoriser into an
    // out-of-band signer (a link is sent to them instead of signing in this browser).
    const [showAuthoriser1, setShowAuthoriser1] = useState(false);
    const [authoriser1Id, setAuthoriser1Id] = useState('');
    const [authoriser1Email, setAuthoriser1Email] = useState('');
    const [showAuthoriser2, setShowAuthoriser2] = useState(false);
    const [authoriser2Id, setAuthoriser2Id] = useState('');
    const [authoriser2Email, setAuthoriser2Email] = useState('');

    // Updated countries array with flag image codes
    const countries = [
        { code: 'SE', name: 'Sweden', flagCode: 'se' },
        { code: 'NO', name: 'Norway', flagCode: 'no' },
        { code: 'DK', name: 'Denmark', flagCode: 'dk' },
        { code: 'FI', name: 'Finland', flagCode: 'fi' }
    ];



    // The SSN is persisted dash-stripped (sanitized for the Psu-Id header), but the
    // SE/DK input validators require the dash — re-insert it for display.
    const formatSsnForDisplay = (ssn, countryCode) => {
        if (!ssn) return '';
        if (countryCode === 'SE' && /^\d{12}$/.test(ssn)) return `${ssn.slice(0, 8)}-${ssn.slice(8)}`;
        if (countryCode === 'DK' && /^\d{10}$/.test(ssn)) return `${ssn.slice(0, 6)}-${ssn.slice(6)}`;
        return ssn;
    };

    // Rehydrate a previous selection (returning after a failed attempt or via the
    // failure redirect) so the user doesn't have to retype everything.
    useEffect(() => {
        try {
            const storedCountry = JSON.parse(sessionStorage.getItem('selectedCountry') || 'null');
            const storedBank = JSON.parse(sessionStorage.getItem('selectedBank') || 'null');
            if (!storedBank?.bicFi) {
                return;
            }
            if (storedCountry?.code) {
                setSelectedCountry(storedCountry.code);
            }
            setSelectedBank(storedBank);
            setSearchTerm(storedBank.name || '');
            if (storedBank.selectedOption) {
                setSelectedBankOption(storedBank.selectedOption);
            }
            fetchBankOptions(storedBank.bicFi);
            if (storedBank.socialSecurityNumber) {
                // Don't prefill — surface the previous SSN as a focus-time suggestion instead.
                setSsnSuggestion(formatSsnForDisplay(storedBank.socialSecurityNumber, storedCountry?.code));
            }
            // Restore the dual-signing authorisers from the persisted creation inputs.
            if (storedBank.bicFi === 'NDEASESS') {
                const authoriserIds = getAuthSession()?.createInputs?.authoriserIds;
                const entries = authoriserIds ? Object.entries(authoriserIds) : [];
                if (entries[0]) {
                    setShowAuthoriser1(true);
                    setAuthoriser1Id(entries[0][0]);
                    setAuthoriser1Email(entries[0][1] || '');
                }
                if (entries[1]) {
                    setShowAuthoriser2(true);
                    setAuthoriser2Id(entries[1][0]);
                    setAuthoriser2Email(entries[1][1] || '');
                }
            }
        } catch (err) {
            console.error('Error restoring previous bank selection:', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch banks from API
    useEffect(() => {
        const fetchBanks = async () => {
            try {
                setLoading(true);
                setBanksError(null);

                const params = getEnvironmentParams();
                if (!params.token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(
                    `${apiUrl}/aspsps?Country=${selectedCountry}`, 
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${params.token}`
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch banks');
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setBanks(data);
                    setFilteredBanks(data);
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching banks:', err);
                setBanksError(t('aspspsLoadBanksError', 'Could not load the list of banks. Please try again.'));
                setLoading(false);
            }
        };

        fetchBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountry, banksFetchAttempt]);

    // Filter banks based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredBanks(banks);
            return;
        }

        const filtered = banks.filter(bank => 
            bank.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBanks(filtered);
        }, [searchTerm, banks]);

    const resetAuthorisers = () => {
        setShowAuthoriser1(false);
        setAuthoriser1Id('');
        setAuthoriser1Email('');
        setShowAuthoriser2(false);
        setAuthoriser2Id('');
        setAuthoriser2Email('');
    };

    // Handle country change
const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setSearchTerm(''); // Clear selected bank text
    setFilteredBanks([]); // Optionally clear filtered banks immediately
    setBankDropdownOpen(false); // Close the bank dropdown if open
    setCountryDropdownOpen(false); // Close the country dropdown if open
    setSelectedBank(null); // Reset selected bank state
    setSelectedBankOption(null); // Reset additional option selection
    setBankOptions([]); // Clear previous bank options
    setSocialSecurityNumber(''); // Clear SSN when country changes
    setSsnSuggestion(''); // The stored SSN was formatted for the old country — drop the suggestion
    setSsnError(''); // Clear SSN error when country changes
    setConsentError(''); // Clear consent error when country changes
    resetAuthorisers(); // Clear dual-signing inputs when country changes
};

    // Add new function to fetch bank options
    const fetchBankOptions = async (bicFi) => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${apiUrl}/aspsps/${bicFi}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${params.token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bank options');
            }

            const data = await response.json();
            setBankOptions(data);
        } catch (error) {
            console.error('Error fetching bank options:', error);
            setBankOptions([]);
        }
    };

    // Modify the bank selection handler
    const handleBankSelect = (bank) => {
        setSearchTerm(bank.name);
        setSelectedBank(bank);
        setBankDropdownOpen(false);
        setSelectedBankOption(null);
        setConsentError(''); // Clear consent error when bank changes
        resetAuthorisers(); // Different bank -> clear any dual-signing inputs
        fetchBankOptions(bank.bicFi);
    };

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target)) {
                setBankDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setCountryDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (bankOptionsDropdownRef.current && !bankOptionsDropdownRef.current.contains(event.target)) {
                setBankOptionsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedCountryData = countries.find(c => c.code === selectedCountry);

    const getSsnPlaceholder = () => {
        switch (selectedCountry) {
            case 'DK':
                return 'DDMMYY-SSSS';
            case 'SE':
                return 'YYYYMMDD-XXXX';
            case 'NO':
                return 'DDMMYYIIICC';
            case 'FI':
                return 'DDMMYYCZZZQ';
            case 'DE':
                return 'XXXXXXXXXXX';
            default:
                return 'XXXXXXXXXXX';
        }
    };

    // Validate SSN format based on country
    const validateSsn = useCallback((ssn, country) => {
        if (!ssn || ssn.trim() === '') {
            return { isValid: false, error: '' };
        }

        const trimmedSsn = ssn.trim();
        let isValid = false;
        let pattern = null;

        switch (country) {
            case 'DK':
                pattern = /^\d{6}-\d{4}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'SE':
                pattern = /^\d{8}-[A-Za-z0-9]{4}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'NO':
                pattern = /^\d{11}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            case 'FI':
                pattern = /^\d{6}[A-Za-z0-9]{5}$/;
                isValid = pattern.test(trimmedSsn);
                break;
            default:
                isValid = trimmedSsn.length > 0;
                break;
        }

        return {
            isValid,
            error: isValid ? '' : (t('aspspsSsnInvalid') || 'Invalid Social security number')
        };
    }, [t]);

    const requireAdditionalInfo = selectedBank;

    // Validate SSN when it changes
    useEffect(() => {
        if (!requireAdditionalInfo) {
            setSsnError('');
            return;
        }

        if (socialSecurityNumber.trim() === '') {
            setSsnError('');
            return;
        }

        const validation = validateSsn(socialSecurityNumber, selectedCountry);
        setSsnError(validation.error);
    }, [socialSecurityNumber, selectedCountry, requireAdditionalInfo, validateSsn]);

    const ssnValidation = requireAdditionalInfo ? validateSsn(socialSecurityNumber, selectedCountry) : { isValid: true, error: '' };
    const isAdditionalInfoValid = !requireAdditionalInfo || (socialSecurityNumber.trim() !== '' && ssnValidation.isValid);

    // Dual signing only applies to Nordea.
    const isNordea = selectedBank?.bicFi === 'NDEASESS';
    // If an authoriser checkbox is ticked, its id must be filled in before continuing.
    const authorisersValid = !isNordea
        || ((!showAuthoriser1 || authoriser1Id.trim() !== '') && (!showAuthoriser2 || authoriser2Id.trim() !== ''));

    const canProceed = Boolean(selectedBank) && Boolean(isAdditionalInfoValid) && authorisersValid;
    const [isCreatingConsent, setIsCreatingConsent] = useState(false);

    // Build the { authoriserId: email|null } dictionary from the ticked + filled authorisers.
    // Returns null when no authoriser is provided (then the flow behaves like any other bank).
    const buildAuthoriserIds = () => {
        if (!isNordea) {
            return null;
        }
        const entries = {};
        if (showAuthoriser1 && authoriser1Id.trim()) {
            entries[authoriser1Id.trim()] = authoriser1Email.trim() || null;
        }
        if (showAuthoriser2 && authoriser2Id.trim()) {
            entries[authoriser2Id.trim()] = authoriser2Email.trim() || null;
        }
        return Object.keys(entries).length > 0 ? entries : null;
    };

    const handleNextClick = async () => {
        if (!canProceed || isCreatingConsent || !selectedBank) {
            return;
        }

        // Clear any previous error
        setConsentError('');

        // Store bank and country data in sessionStorage for the downstream screens.
        const countryData = countries.find(c => c.code === selectedCountry);
        const sanitizedSsn = socialSecurityNumber.replace(/-/g, '') || null;
        const bankDataWithExtras = {
            ...selectedBank,
            selectedOption: selectedBankOption ? { id: selectedBankOption.id, name: selectedBankOption.name } : null,
            socialSecurityNumber: sanitizedSsn
        };
        sessionStorage.setItem('selectedBank', JSON.stringify(bankDataWithExtras));
        sessionStorage.setItem('selectedCountry', JSON.stringify(countryData));

        setIsCreatingConsent(true);
        try {
            // NDEASESS with one or two named authorisers => dual signing. Any other bank (or
            // NDEASESS with no authoriser entered) => null dict, single-authoriser flow.
            const authoriserIds = buildAuthoriserIds();
            const isDual = !!authoriserIds;

            const createInputs = {
                bicFi: bankDataWithExtras.bicFi,
                ssn: sanitizedSsn,
                authoriserIds,
                aspspsId: bankDataWithExtras.selectedOption?.id || null,
            };

            // Start with force:false (reuse/rehydrate an existing consent). If that fails —
            // e.g. a previous attempt left an un-authorised consent colliding at the
            // provider — retry once with force:true, which makes the backend terminate
            // the old consent and create a fresh one.
            let responseArray;
            try {
                responseArray = await startOrchestration({ createInputs, force: false });
            } catch (reuseErr) {
                console.error('Consent reuse failed, retrying with a forced new consent:', reuseErr);
                responseArray = await startOrchestration({ createInputs, force: true });
            }
            buildAuthSession(responseArray, createInputs, { isDual });

            // A reused consent can come back already authorised (every scaStatus
            // "finalised"). Single signing: nothing left to authenticate — go straight
            // to account selection. Dual signing still goes to the coordinator, which
            // skips finalised authorisers and confirms the consent itself.
            const alreadyFinalised = responseArray.length > 0 && responseArray.every(
                (r) => String(r.scaStatus || '').toLowerCase() === 'finalised'
            );
            if (!isDual && alreadyFinalised) {
                navigate('/pick-account');
                return;
            }

            // Dual signing goes to the coordinator; single-authoriser picks a method directly.
            navigate(isDual ? '/sign-status' : '/select-method');
        } catch (err) {
            console.error('Error creating consent:', err);
            // Set error message and stay on the same page
            setConsentError(t('bankConnErrorMessage') || 'Something went wrong. Please try again.');
        } finally {
            setIsCreatingConsent(false);
        }
    };

    const navigationProps = {
        previousPath: "/kyc-complete",
        onNext: handleNextClick,
        nextDisabled: !canProceed || isCreatingConsent
    };

    if (loading) {
        return <div className="loading">{t('aspspsLoading')}</div>;
    }

    return (
        <PageLayout currentStep={2} navigationProps={navigationProps} 
        infoText="aspspsInfoText">
            <div className="widget-content">
                <h1 className="main-title">{t('aspspsTitle')}</h1>
                <p className="main-subtitle">
                    {t('aspspsSubtitle1')}
                    <br />
                    {t('aspspsSubtitle2')}
                </p>

                {banksError && (
                    <div className="banks-error-message" role="alert">
                        <span>{banksError}</span>
                        <button
                            type="button"
                            className="banks-retry-btn"
                            onClick={() => setBanksFetchAttempt((attempt) => attempt + 1)}
                        >
                            {t('aspspsRetryBanks', 'Try again')}
                        </button>
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="country-select">{t('aspspsCountryLabel')}</label>
                    <div className="custom-country-select-wrapper" ref={countryDropdownRef}>
                        <button
                            type="button"
                            id="country-select"
                            className="custom-country-select-header"
                            onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                            aria-expanded={countryDropdownOpen}
                            aria-haspopup="true"
                            aria-controls="country-select-options"
                        >
                            <span className={`fi fi-${selectedCountryData.flagCode}`}></span>
                            <span>{t(selectedCountryData.name)}</span>
                            <span className="select-arrow">▼</span>
                        </button>
                        {countryDropdownOpen && (
                            <div className="custom-country-select-options" id="country-select-options">
                                {countries.map((country, index) => (
                                    <button
                                        type="button"
                                        key={country.code}
                                        className={`custom-country-select-option${selectedCountry === country.code ? ' selected' : ''}`}
                                        onClick={() => {
                                            handleCountryChange(country.code);
                                        }}
                                    >
                                        <span className={`fi fi-${country.flagCode}`}></span>
                                        <span>{t(country.name)}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-group">
                <div className="form-group">
                            <label htmlFor="ssn-input">
                                {t('aspspsSsnLabel')}
                                <span
                                    className="tooltip-icon"
                                    aria-label={t('aspspsSsnTooltip')}
                                    data-tooltip={t('aspspsSsnTooltip')}
                                >
                                    i
                                </span>
                            </label>
                            <div className="ssn-input-wrapper">
                                <input
                                    id="ssn-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    className={`ssn-input ${ssnError ? 'ssn-input-error' : ''}`}
                                    value={socialSecurityNumber}
                                    onChange={(e) => setSocialSecurityNumber(e.target.value)}
                                    onFocus={() => setSsnFocused(true)}
                                    onBlur={() => setSsnFocused(false)}
                                    placeholder={getSsnPlaceholder()}
                                    required={requireAdditionalInfo}
                                    aria-invalid={ssnError ? 'true' : 'false'}
                                    aria-describedby={ssnError ? 'ssn-error' : undefined}
                                />
                                {ssnFocused
                                    && ssnSuggestion
                                    && ssnSuggestion !== socialSecurityNumber
                                    && ssnSuggestion.startsWith(socialSecurityNumber) && (
                                    <div className="ssn-suggestion-dropdown">
                                        <button
                                            type="button"
                                            className="ssn-suggestion-option"
                                            // Fill on mousedown: it fires before the input's blur,
                                            // so the dropdown can't unmount under the click.
                                            // preventDefault keeps focus on the input.
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setSocialSecurityNumber(ssnSuggestion);
                                            }}
                                            onClick={() => setSocialSecurityNumber(ssnSuggestion)}
                                            data-testid="ssn-suggestion"
                                        >
                                            {ssnSuggestion}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {ssnError && (
                                <span id="ssn-error" className="ssn-error-message" role="alert">
                                    {ssnError}
                                </span>
                            )}

                        </div>
                    <label htmlFor="bank-select">{t('aspspsBankLabel')}</label>
                    <div className="custom-bank-select-wrapper" ref={bankDropdownRef}>
                        <button
                            type="button"
                            id="bank-select"
                            className="custom-bank-select-header"
                            onClick={() => setBankDropdownOpen(!bankDropdownOpen)}
                            aria-expanded={bankDropdownOpen}
                            aria-haspopup="true"
                            aria-controls="bank-select-options"
                        >
                            {searchTerm && (
                                <>
                                    <img
                                        src={filteredBanks.find(b => b.name === searchTerm)?.logoUrl}
                                        alt={searchTerm}
                                        className="bank-logo"
                                        style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle' }}
                                    />
                                    <span>{searchTerm}</span>
                                </>
                            )}
                            {!searchTerm && <span>{t('aspspsSelectYourBank')}</span>}
                            <span className="select-arrow">▼</span>
                        </button>
                        {bankDropdownOpen && (
                            <div className="custom-bank-select-options" id="bank-select-options">
                                {banks.map((bank, index) => (
                                    <button
                                        type="button"
                                        key={`${bank.name}-${bank.country}`}
                                        className={`custom-bank-select-option${selectedBank && selectedBank.name === bank.name ? " selected" : ""}`} 
                                        onClick={() => handleBankSelect(bank)}
                                    >
                                        <img
                                            src={bank.logoUrl}
                                            alt={bank.name}
                                            className="bank-logo"
                                            style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle' }}
                                        />
                                        <span>{bank.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Add new bank options dropdown */}
                {selectedBank && bankOptions.length > 0 && (
                        <div className="form-group">
                            <label htmlFor="bank-options-select">{t('aspspsSelectOption')}</label>
                            <div className="custom-bank-select-wrapper" ref={bankOptionsDropdownRef}>
                                <button
                                    type="button"
                                    id="bank-options-select"
                                    className="custom-bank-select-header"
                                    onClick={() => setBankOptionsDropdownOpen(!bankOptionsDropdownOpen)}
                                    aria-expanded={bankOptionsDropdownOpen}
                                    aria-haspopup="true"
                                    aria-controls="bank-options-select-options"
                                >
                                    {selectedBankOption ? selectedBankOption.name : t('aspspsSelectOption')}
                                    <span className="select-arrow">{bankOptionsDropdownOpen ? "▲" : "▼"}</span>
                                </button>
                                {bankOptionsDropdownOpen && (
                                    <div className="custom-bank-select-options" id="bank-options-select-options">
                                        {bankOptions.map((option, index) => (
                                            <button
                                                type="button"
                                                key={option.id}
                                                className={`custom-bank-select-option${selectedBankOption && selectedBankOption.id === option.id ? " selected" : ""}`}
                                                onClick={() => {
                                                    setSelectedBankOption(option);
                                                    setBankOptionsDropdownOpen(false);
                                                }}
                                            >
                                                {option.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                )}

                {/* Dual signing — Nordea only. Name one or two authorisers; an optional
                    email turns that authoriser into an out-of-band (emailed-link) signer. */}
                {isNordea && (
                    <div className="form-group dual-signing-section">
                        <label className="dual-signing-title">{t('aspspsDualTitle', 'Authorisers')}</label>
                        <p className="dual-signing-subtitle">
                            {t('aspspsDualSubtitle', 'This bank may require one or two people to authorise the connection.')}
                        </p>

                        <label className="dual-authoriser-checkbox">
                            <input
                                type="checkbox"
                                checked={showAuthoriser1}
                                onChange={(e) => {
                                    setShowAuthoriser1(e.target.checked);
                                    if (!e.target.checked) {
                                        setAuthoriser1Id('');
                                        setAuthoriser1Email('');
                                    }
                                }}
                                data-testid="dual-authoriser-checkbox-1"
                            />
                            <span>{t('aspspsAddAuthoriser', 'Add an authoriser')}</span>
                        </label>
                        {showAuthoriser1 && (
                            <div className="dual-authoriser-inputs">
                                <input
                                    type="text"
                                    className="ssn-input"
                                    aria-label={t('aspspsAuthoriserIdLabel', 'Authoriser ID')}
                                    placeholder={t('aspspsAuthoriserIdLabel', 'Authoriser ID')}
                                    value={authoriser1Id}
                                    onChange={(e) => setAuthoriser1Id(e.target.value)}
                                    data-testid="authoriser-id-input-1"
                                />
                                <input
                                    type="email"
                                    className="ssn-input"
                                    aria-label={t('aspspsAuthoriserEmailLabel', 'Authoriser email (optional)')}
                                    placeholder={t('aspspsAuthoriserEmailLabel', 'Authoriser email (optional)')}
                                    value={authoriser1Email}
                                    onChange={(e) => setAuthoriser1Email(e.target.value)}
                                    data-testid="authoriser-email-input-1"
                                />
                            </div>
                        )}

                        <label className="dual-authoriser-checkbox">
                            <input
                                type="checkbox"
                                checked={showAuthoriser2}
                                onChange={(e) => {
                                    setShowAuthoriser2(e.target.checked);
                                    if (!e.target.checked) {
                                        setAuthoriser2Id('');
                                        setAuthoriser2Email('');
                                    }
                                }}
                                data-testid="dual-authoriser-checkbox-2"
                            />
                            <span>{t('aspspsAddSecondAuthoriser', 'Add a second authoriser')}</span>
                        </label>
                        {showAuthoriser2 && (
                            <div className="dual-authoriser-inputs">
                                <input
                                    type="text"
                                    className="ssn-input"
                                    aria-label={t('aspspsAuthoriser2IdLabel', 'Second authoriser ID')}
                                    placeholder={t('aspspsAuthoriser2IdLabel', 'Second authoriser ID')}
                                    value={authoriser2Id}
                                    onChange={(e) => setAuthoriser2Id(e.target.value)}
                                    data-testid="authoriser-id-input-2"
                                />
                                <input
                                    type="email"
                                    className="ssn-input"
                                    aria-label={t('aspspsAuthoriser2EmailLabel', 'Second authoriser email (optional)')}
                                    placeholder={t('aspspsAuthoriser2EmailLabel', 'Second authoriser email (optional)')}
                                    value={authoriser2Email}
                                    onChange={(e) => setAuthoriser2Email(e.target.value)}
                                    data-testid="authoriser-email-input-2"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Error message for consent creation failure */}
                {consentError && (
                    <div className="consent-error-message" role="alert">
                        {consentError}
                    </div>
                )}
            </div>
        </PageLayout>
    );
}

export default AspspsList;